import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import {
  getBenchmarkCatalog,
  getBenchmarkSleeveComparison,
  getBenchmarkSleeveComparisonSubPort,
  SleeveComparisonParams,
} from '../composables/fetchAPI';
import toast from 'react-hot-toast';
import SearchBar from './SearchBar';
import { cryptoPairLabel, formatCryptoSymbolsInText } from '../composables/fetchCrypto';
import { usePortfolio } from '../context/PortfolioContext';

const OVERVIEW_VALUE = '__ALL__';
const MAX_SYMBOLS = 8;
const DEBOUNCE_MS = 300;

const RANGE_OPTIONS: [string, string][] = [
  ['1W', '1W'], ['1M', '1M'], ['3M', '3M'], ['6M', '6M'], ['YTD', 'YTD'],
  ['1Y', '1Y'], ['3Y', '3Y'], ['5Y', '5Y'], ['10Y', '10Y'], ['ALL', 'ALL'],
  
];

const DEFAULT_TIMEFRAME = 'DAY';

const ERROR_MESSAGES: Record<string, string> = {
  INVALID_SYMBOL: 'ไม่พบสัญลักษณ์ที่รองรับ',
  INSUFFICIENT_HISTORY: 'ข้อมูลย้อนหลังไม่เพียงพอ',
  PRICE_UNAVAILABLE: 'ไม่มีข้อมูลราคาในช่วงเวลานี้',
  UPSTREAM_ERROR: 'ไม่สามารถโหลดข้อมูลตลาดได้',
  INSUFFICIENT_CAPITAL: 'มูลค่า Benchmark ไม่เพียงพอสำหรับจำลองกระแสเงิน',
};

// error.code เหล่านี้แปลว่าพอร์ต (หรือ sub-port ที่เลือก) ยังไม่มีธุรกรรม/หุ้นเลย
// ไม่ใช่ error จริงๆ ที่ควรให้ user กด "ลองอีกครั้ง" แต่ควรบอกให้ไปเพิ่มรายการซื้อขายก่อน
const EMPTY_PORTFOLIO_CODES = new Set(['PORTFOLIO_SLEEVE_EMPTY']);

// จำค่าสินทรัพย์เปรียบเทียบ / ช่วงเวลา / ความละเอียดข้อมูลที่ผู้ใช้เลือกไว้ล่าสุด ไว้ใน localStorage
// เพื่อให้เปิดหน้ามาใหม่แล้วค่ายังคงเดิม (ไม่ผูกกับ portfolioId เพราะเป็นค่า preference ของผู้ใช้ ไม่ใช่ของพอร์ต)
const BENCHMARK_PREFS_KEY = 'wti_benchmark_prefs_v1';

interface BenchmarkPrefs {
  selectedSymbols?: string[];
  range?: string;
}

function loadBenchmarkPrefs(): BenchmarkPrefs {
  try {
    const raw = localStorage.getItem(BENCHMARK_PREFS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return {
      selectedSymbols: Array.isArray(parsed?.selectedSymbols)
        ? parsed.selectedSymbols.filter((s: unknown): s is string => typeof s === 'string')
        : undefined,
      range: typeof parsed?.range === 'string' ? parsed.range : undefined,
    };
  } catch {
    return {};
  }
}

const CATALOG_FALLBACK: CatalogItem[] = [
  { symbol: 'SPY', displayName: 'S&P 500', description: 'ตัวแทนบริษัทขนาดใหญ่ชั้นนำของสหรัฐฯ' },
  { symbol: 'QQQ', displayName: 'Nasdaq-100', description: 'เน้นกลุ่มเทคโนโลยีและหุ้นเติบโต' },
  { symbol: 'IWM', displayName: 'Russell 2000', description: 'ตัวแทนบริษัทขนาดเล็กของสหรัฐฯ' },
  { symbol: 'DIA', displayName: 'Dow Jones', description: 'บริษัทขนาดใหญ่และมีชื่อเสียง 30 แห่ง' },
  { symbol: 'MDY', displayName: 'S&P MidCap 400', description: 'ตัวแทนบริษัทขนาดกลางของสหรัฐฯ' },
];

const PORTFOLIO_COLOR = '#facc15';

// พาเลต 10 สีที่แยกออกจากกันชัดเจนบนพื้นหลังมืด และเลี่ยงโทนเหลือง/อำพันที่กันไว้ให้ PORTFOLIO_COLOR โดยเฉพาะ
const BENCHMARK_PALETTE = [
  '#60A5FA', // blue
  '#34D399', // emerald
  '#FB923C', // orange
  '#FB7185', // rose
  '#A78BFA', // violet
  '#2DD4BF', // teal
  '#F472B6', // pink
  '#38BDF8', // sky
  '#A3E635', // lime
  '#E879F9', // fuchsia
];

function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  const value = parseInt(full, 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

interface CatalogItem {
  symbol: string;
  displayName: string;
  description?: string;
}

interface ComparisonSymbolMeta {
  symbol: string;
  displayName?: string;
  fullName?: string;
  type?: string;
}

interface SeriesRow {
  date: string;
  values: Record<string, { percentValue?: number; realValue?: number } | undefined>;
}

interface ComparisonRange {
  baselineDate?: string;
  effectiveFrom?: string;
  to?: string;
}

interface ComparisonData {
  mode: string;
  metric: string | null;
  benchmarkReturnPolicy: string | null;
  range: ComparisonRange | null;
  symbols: ComparisonSymbolMeta[];
  series: SeriesRow[];
  errors: { symbol?: string; code?: string }[];
}

const normalizeSymbol = (value: string): string =>
  String(value || '').trim().toUpperCase().replace(/\s+/g, '');

const formatDate = (dateString?: string | null): string => {
  if (!dateString) return '—';
  const d = new Date(`${dateString}T00:00:00`);
  if (isNaN(d.getTime())) return dateString;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatDateShort = (dateString?: string | null): string => {
  if (!dateString) return '';
  const d = new Date(`${dateString}T00:00:00`);
  if (isNaN(d.getTime())) return dateString;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
};

const formatPercent = (value: number | null | undefined): string => {
  const v = typeof value === 'number' && !isNaN(value) ? value : null;
  if (v === null) return '—';
  const decimals = Math.abs(v) >= 100 ? 1 : 2;
  return `${v >= 0 ? '+' : ''}${v.toFixed(decimals)}%`;
};

const formatUSD = (value: number | null | undefined): string => {
  const v = typeof value === 'number' && !isNaN(value) ? value : null;
  if (v === null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);
};

const getReturnColor = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || isNaN(value) || value === 0) return 'var(--text-color)';
  return value > 0 ? 'var(--green-text-color)' : 'var(--red-text-color)';
};

// safe modulo กัน index ติดลบ (เช่น symbol ที่เพิ่งถูกลบออกจาก selectedSymbols แต่ comparison ยังไม่ refetch)
const benchmarkColor = (index: number): string =>
  BENCHMARK_PALETTE[((index % BENCHMARK_PALETTE.length) + BENCHMARK_PALETTE.length) % BENCHMARK_PALETTE.length];

const mapSymbolError = (code?: string): string => ERROR_MESSAGES[code || ''] || `เกิดข้อผิดพลาด (${code || 'UNKNOWN_ERROR'})`;

// Backend อาจห่อ response สำเร็จเป็น { success, data, error } หรือส่ง payload ตรงๆ ที่ top-level
// ต้องเช็ค success === false เองเพราะ HTTP status อาจเป็น 200 ทั้งที่ backend ปฏิเสธคำขอ (เช่น validate ไม่ผ่าน)
function unwrapApiPayload(response: any, fallbackMessage: string): any {
  if (
    response &&
    typeof response === 'object' &&
    !Array.isArray(response) &&
    Object.prototype.hasOwnProperty.call(response, 'success')
  ) {
    if (response.success === false) {
      const message =
        response?.error?.th_message ||
        response?.error?.message ||
        response?.th_message ||
        response?.message ||
        fallbackMessage;
      throw new Error(message);
    }
    if (Object.prototype.hasOwnProperty.call(response, 'data')) {
      return response.data;
    }
  }
  return response;
}

function getSuccessfulSymbols(data: ComparisonData): ComparisonSymbolMeta[] {
  const failed = new Set((data.errors || []).map((e) => e?.symbol).filter(Boolean));
  return (data.symbols || []).filter((meta) => {
    if (!meta?.symbol || failed.has(meta.symbol)) return false;
    return (data.series || []).some((row) => {
      const v = row?.values?.[meta.symbol];
      return v && Number.isFinite(Number(v.percentValue));
    });
  });
}

function getTerminalValue(data: ComparisonData, symbol: string): { percentValue: number | null; realValue: number | null } | null {
  for (let i = data.series.length - 1; i >= 0; i -= 1) {
    const v = data.series[i]?.values?.[symbol];
    if (v && (Number.isFinite(Number(v.percentValue)) || Number.isFinite(Number(v.realValue)))) {
      return {
        percentValue: Number.isFinite(Number(v.percentValue)) ? Number(v.percentValue) : null,
        realValue: Number.isFinite(Number(v.realValue)) ? Number(v.realValue) : null,
      };
    }
  }
  return null;
}

function makeDataset(meta: ComparisonSymbolMeta, index: number, data: ComparisonData, portfolioLabel?: string): any {
  const isPortfolio = meta.symbol === 'PORTFOLIO' || meta.type === 'PORTFOLIO';
  const color = isPortfolio ? PORTFOLIO_COLOR : benchmarkColor(index);

  const points = data.series.map((row) => {
    const v = row?.values?.[meta.symbol];
    return v && Number.isFinite(Number(v.percentValue)) ? Number(v.percentValue) : null;
  });

  const realValues = data.series.map((row) => {
    const v = row?.values?.[meta.symbol];
    return v && Number.isFinite(Number(v.realValue)) ? Number(v.realValue) : null;
  });

  // คริปโต: แสดง X:BTCUSD เป็น BTC/USD (ค่าที่ส่งหลังบ้านยังเป็น X:BTCUSD)
  const displayName = isPortfolio && portfolioLabel ? portfolioLabel : formatCryptoSymbolsInText(meta.displayName || meta.symbol);

  return {
    label: `${displayName} (${cryptoPairLabel(meta.symbol)})`,
    data: points,
    borderColor: color,
    backgroundColor: color,
    borderWidth: isPortfolio ? 3.5 : 2.5,
    pointRadius: 0,
    pointHoverRadius: isPortfolio ? 6 : 5,
    pointHitRadius: 12,
    tension: 0.2,
    spanGaps: false,
    fill: false,
    symbol: meta.symbol,
    displayName,
    realValues,
    order: isPortfolio ? 0 : index + 1,
  };
}

const BenchmarkComponent: React.FC<{ portfolios?: any[]; portfolioId: string; portfolioName?: string }> = ({
  portfolios = [],
  portfolioId,
  portfolioName,
}) => {
  const { displayMode } = usePortfolio();
  const [selectedSubPortId, setSelectedSubPortId] = useState<string>(OVERVIEW_VALUE);

  const [catalog, setCatalog] = useState<CatalogItem[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogUsingFallback, setCatalogUsingFallback] = useState(false);
  const [catalogReloadTick, setCatalogReloadTick] = useState(0);

  // โหลด preference ที่เคยจำไว้ (ถ้ามี) แค่ครั้งเดียวตอน mount
  const initialPrefsRef = useRef<BenchmarkPrefs | null>(null);
  if (initialPrefsRef.current === null) {
    initialPrefsRef.current = loadBenchmarkPrefs();
  }
  const initialPrefs = initialPrefsRef.current;

  const [selectedSymbols, setSelectedSymbols] = useState<string[]>(
    Array.isArray(initialPrefs.selectedSymbols) ? initialPrefs.selectedSymbols : ['SPY', 'QQQ']
  );

  const [range, setRange] = useState<string>(initialPrefs.range || 'ALL');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [comparison, setComparison] = useState<ComparisonData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [comparisonError, setComparisonError] = useState<string | null>(null);
  const [comparisonErrorCode, setComparisonErrorCode] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const requestSeqRef = useRef(0);

  const rangeValidationError = useMemo(() => {
    if (range !== 'CUSTOM') return '';
    if (!fromDate || !toDate) return 'กรุณาเลือกวันที่เริ่มต้นและวันที่สิ้นสุด';
    if (fromDate > toDate) return 'วันที่เริ่มต้นต้องไม่อยู่หลังวันที่สิ้นสุด';
    return '';
  }, [range, fromDate, toDate]);

  // จำสินทรัพย์เปรียบเทียบ / ช่วงเวลาไว้ใน localStorage ทุกครั้งที่มีการเพิ่ม/ลบ/เปลี่ยน
  useEffect(() => {
    try {
      localStorage.setItem(
        BENCHMARK_PREFS_KEY,
        JSON.stringify({ selectedSymbols, range })
      );
    } catch {
      // เช่น private mode หรือ storage เต็ม ไม่ต้องทำอะไรต่อ ไม่ใช่ error ที่กระทบการใช้งานหลัก
    }
  }, [selectedSymbols, range]);

  // โหลด catalog ของ Benchmark ยอดนิยม
  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        setCatalogLoading(true);
        setCatalogUsingFallback(false);

        const response = await getBenchmarkCatalog();
        const payload = unwrapApiPayload(response, 'ไม่สามารถโหลด catalog ได้');
        const items = Array.isArray(payload?.items) ? payload.items : Array.isArray(payload) ? payload : [];

        if (!items.length) throw new Error('API ไม่ได้ส่งรายการ Benchmark กลับมา');
        if (!isMounted) return;

        setCatalog(
          items
            .filter((item: any) => item && typeof item.symbol === 'string' && item.symbol.trim())
            .map((item: any) => ({
              symbol: normalizeSymbol(item.symbol),
              displayName: item.displayName || item.symbol,
              description: item.description || '',
            }))
        );
      } catch (error) {
        console.error('Error fetching benchmark catalog:', error);
        if (!isMounted) return;
        setCatalog(CATALOG_FALLBACK);
        setCatalogUsingFallback(true);
      } finally {
        if (isMounted) setCatalogLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [catalogReloadTick]);

  // โหลดข้อมูลเปรียบเทียบ (sleeve-comparison) แบบ debounce ตาม filter ที่เลือก
  useEffect(() => {
    if (!portfolioId) return;

    if (range === 'CUSTOM' && rangeValidationError) {
      setComparison(null);
      setComparisonError(null);
      setComparisonErrorCode(null);
      return;
    }

    const timer = window.setTimeout(async () => {
      const seq = ++requestSeqRef.current;

      try {
        setIsLoading(true);
        setComparisonError(null);
        setComparisonErrorCode(null);

        const params: SleeveComparisonParams = {
          symbols: selectedSymbols,
          range,
          timeframe: DEFAULT_TIMEFRAME,
          mode: 'BY_DATE',
          priceMode: 'CLOSE_PRICE',
        };

        if (range === 'CUSTOM') {
          params.from = fromDate;
          params.to = toDate;
        }

        const response = selectedSubPortId === OVERVIEW_VALUE
          ? await getBenchmarkSleeveComparison(portfolioId, params)
          : await getBenchmarkSleeveComparisonSubPort(portfolioId, selectedSubPortId, params);

        if (seq !== requestSeqRef.current) return;

        const payload = unwrapApiPayload(response, 'ไม่สามารถโหลดข้อมูลเปรียบเทียบได้');

        if (!payload || payload.mode !== 'BY_DATE') {
          throw new Error('รูปแบบข้อมูลจาก API ไม่ถูกต้อง');
        }

        setComparison({
          mode: payload.mode,
          metric: typeof payload.metric === 'string' ? payload.metric : null,
          benchmarkReturnPolicy: typeof payload.benchmarkReturnPolicy === 'string' ? payload.benchmarkReturnPolicy : null,
          range: payload.range || null,
          symbols: Array.isArray(payload.symbols) ? payload.symbols : [],
          series: Array.isArray(payload.series) ? payload.series : [],
          errors: Array.isArray(payload.errors) ? payload.errors : [],
        });
      } catch (error: any) {
        if (seq !== requestSeqRef.current) return;
        console.error('Error fetching sleeve comparison:', error);
        setComparisonError(error?.th_message || error?.message || 'ไม่สามารถโหลดข้อมูลเปรียบเทียบได้');
        setComparisonErrorCode(error?.code ? String(error.code) : null);
      } finally {
        if (seq === requestSeqRef.current) setIsLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [portfolioId, selectedSubPortId, selectedSymbols, range, fromDate, toDate, reloadTick, rangeValidationError]);

  // ใช้ชื่อพอร์ต (และชื่อ sub-port ถ้าเลือกอยู่) แทนคำว่า "PORTFOLIO" ทุกที่ที่แสดงผล
  const portfolioDisplayLabel = useMemo(() => {
    const base = portfolioName?.trim() || 'PORTFOLIO';
    if (selectedSubPortId === OVERVIEW_VALUE) return base;
    const subPort = portfolios.find((p: any) => p.subPortId === selectedSubPortId);
    const subName = subPort?.name || subPort?.subPortId || selectedSubPortId;
    return `${base} · ${subName}`;
  }, [portfolioName, portfolios, selectedSubPortId]);

  const successfulSymbols = useMemo(() => (comparison ? getSuccessfulSymbols(comparison) : []), [comparison]);
  const hasChartData = Boolean(comparison && comparison.series.length && successfulSymbols.length);

  const chartData = useMemo(() => {
    if (!comparison || !hasChartData) return null;
    const labels = comparison.series.map((row) => row.date);
    // ใช้ตำแหน่งใน selectedSymbols (ลำดับที่ผู้ใช้เลือก) แทนตำแหน่งใน successfulSymbols
    // เพราะ successfulSymbols มาจากลำดับที่ backend ส่งกลับมา ซึ่งอาจสลับตำแหน่งเมื่อเพิ่ม/ลบ symbol
    // ทำให้สีของเส้นเดิมเปลี่ยนไปโดยไม่ตั้งใจ ต้อง fix สีตาม symbol ไม่ใช่ตามตำแหน่งใน response
    const datasets = successfulSymbols.map((meta) =>
      makeDataset(meta, selectedSymbols.indexOf(meta.symbol), comparison, portfolioDisplayLabel)
    );
    return { labels, datasets };
  }, [comparison, hasChartData, successfulSymbols, selectedSymbols, portfolioDisplayLabel]);

  const themeColors = useMemo(() => {
    const el = containerRef.current;
    const read = (varName: string, fallback: string) => {
      if (!el) return fallback;
      const v = getComputedStyle(el).getPropertyValue(varName).trim();
      return v || fallback;
    };
    return {
      text: read('--text-color', '#98a2b3'),
      grid: read('--border-item-light', 'rgba(152,162,179,0.18)'),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 260 as const },
      interaction: { mode: 'index' as const, intersect: false },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: themeColors.text,
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 8,
            font: { size: 13 },
            callback(this: any, value: any) {
              return formatDateShort(this.getLabelForValue(value));
            },
          },
        },
        y: {
          grid: { color: themeColors.grid },
          ticks: {
            color: themeColors.text,
            font: { size: 13 },
            callback: (value: any) => `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`,
          },
        },
      },
      plugins: {
        legend: {
          position: 'top' as const,
          align: 'start' as const,
          labels: {
            color: themeColors.text,
            usePointStyle: true,
            boxWidth: 14,
            boxHeight: 10,
            padding: 16,
            font: { size: 13, weight: 600 as const },
          },
        },
        tooltip: {
          mode: 'index' as const,
          intersect: false,
          padding: 12,
          titleFont: { size: 14, weight: 700 as const },
          bodyFont: { size: 13 },
          callbacks: {
            title: (items: any[]) => formatDate(items?.[0]?.label),
            label: (ctx: any) => {
              const ds = ctx.dataset;
              const percent = ctx.parsed.y;
              const real = ds.realValues?.[ctx.dataIndex];
              const realPart = Number.isFinite(real) ? ` · ${formatUSD(real)}` : '';
              return `${ds.displayName} (${cryptoPairLabel(ds.symbol)}): ${formatPercent(percent)}${realPart}`;
            },
          },
        },
      },
    }),
    [themeColors]
  );

  const handleSymbolSelect = (rawSymbol: string) => {
    const symbol = normalizeSymbol(rawSymbol);
    if (!symbol) return;

    if (symbol === 'PORTFOLIO') {
      toast.error('PORTFOLIO ถูกรวมอยู่ในกราฟโดยอัตโนมัติแล้ว');
      return;
    }

    if (selectedSymbols.includes(symbol)) {
      toast.error(`${cryptoPairLabel(symbol)} ถูกเลือกอยู่แล้ว`);
      return;
    }

    if (selectedSymbols.length >= MAX_SYMBOLS) {
      toast.error(`เลือกได้สูงสุด ${MAX_SYMBOLS} symbols`);
      return;
    }

    setSelectedSymbols((prev) => [...prev, symbol]);
  };

  const handleRemoveSymbol = (symbol: string) => {
    setSelectedSymbols((prev) => prev.filter((s) => s !== symbol));
  };

  const handleToggleCatalogSymbol = (symbol: string) => {
    if (selectedSymbols.includes(symbol)) {
      handleRemoveSymbol(symbol);
      return;
    }

    if (selectedSymbols.length >= MAX_SYMBOLS) {
      toast.error(`เลือกได้สูงสุด ${MAX_SYMBOLS} symbols`);
      return;
    }

    setSelectedSymbols((prev) => [...prev, symbol]);
  };

  const handleSelectRange = (value: string) => {
    setRange(value);

    if (value === 'CUSTOM') {
      setToDate((prevTo) => prevTo || new Date().toISOString().slice(0, 10));
      setFromDate((prevFrom) => {
        if (prevFrom) return prevFrom;
        const base = new Date();
        base.setFullYear(base.getFullYear() - 1);
        return base.toISOString().slice(0, 10);
      });
    }
  };

  const getSymbolMeta = (symbol: string): ComparisonSymbolMeta => {
    const fromComparison = comparison?.symbols?.find((s) => s?.symbol === symbol);
    if (fromComparison) return fromComparison;

    const fromCatalog = catalog.find((c) => c.symbol === symbol);
    if (fromCatalog) return { symbol, displayName: fromCatalog.displayName, type: 'INDEX' };

    return { symbol, displayName: symbol, type: 'SYMBOL' };
  };

  const rangeMetaText = useMemo(() => {
    const r = comparison?.range;
    if (!r) return '';
    const parts: string[] = [];
    if (r.baselineDate) parts.push(`Baseline ${formatDate(r.baselineDate)}`);
    if (r.effectiveFrom) parts.push(`เริ่ม ${formatDate(r.effectiveFrom)}`);
    if (r.to) parts.push(`ถึง ${formatDate(r.to)}`);
    return parts.join(' · ');
  }, [comparison]);

  const summaryEntries = useMemo(() => {
    if (!comparison || !hasChartData) return [];

    // ใช้ตำแหน่งใน selectedSymbols (ไม่ใช่ตำแหน่งใน successfulSymbols/response ของ backend) เพื่อให้สีของการ์ด
    // คงเดิมตาม symbol เสมอ ไม่ว่าจะเพิ่ม/ลบ symbol อื่นแล้วลำดับที่ backend ส่งกลับมาจะเปลี่ยนไปแค่ไหนก็ตาม
    const entries = successfulSymbols
      .map((meta) => ({ meta, index: selectedSymbols.indexOf(meta.symbol), terminal: getTerminalValue(comparison, meta.symbol) }))
      .filter((entry): entry is { meta: ComparisonSymbolMeta; index: number; terminal: { percentValue: number | null; realValue: number | null } } => Boolean(entry.terminal));

    const finiteReturns = entries.map((e) => e.terminal.percentValue).filter((v): v is number => Number.isFinite(v));
    const bestReturn = finiteReturns.length ? Math.max(...finiteReturns) : null;

    const portfolioEntry = entries.find((e) => e.meta.symbol === 'PORTFOLIO' || e.meta.type === 'PORTFOLIO');
    const portfolioReturn = portfolioEntry?.terminal.percentValue ?? null;

    return entries.map((entry) => {
      const isPortfolio = entry.meta.symbol === 'PORTFOLIO' || entry.meta.type === 'PORTFOLIO';
      const isBest = Number.isFinite(bestReturn) && entry.terminal.percentValue === bestReturn;
      const diff =
        !isPortfolio && Number.isFinite(entry.terminal.percentValue) && Number.isFinite(portfolioReturn)
          ? (entry.terminal.percentValue as number) - (portfolioReturn as number)
          : null;
      const color = isPortfolio ? PORTFOLIO_COLOR : benchmarkColor(entry.index);

      return { ...entry, isPortfolio, isBest, diff, color };
    });
  }, [comparison, hasChartData, successfulSymbols, selectedSymbols]);

  return (
    <div
      ref={containerRef}
      className="p-5 md:p-7 rounded-lg shadow-lg"
      style={{ backgroundColor: 'var(--component-bg-color)', color: 'var(--text-color)' }}
    >
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-5">
        <div>
          <h2 className="relative inline-block text-2xl md:text-3xl font-bold" style={{ color: 'var(--text-color)' }}>
            Portfolio Benchmark
            <span className="absolute -top-2 -right-9 px-1 rounded-lg text-[10px] font-bold tracking-wide text-white bg-blue-500 animate-pulse">
              BETA
            </span>
          </h2>
          <p className="text-sm md:text-base mt-1.5" style={{ color: 'var(--text-color)' }}>
            {/* ไม่แสดงชื่อ metric / return policy (INVESTED_SLEEVE_TWR · PRICE_RETURN) — แสดงเฉพาะช่วงวันที่ */}
            {rangeMetaText}
          </p>
        </div>
        {portfolios.length > 0 && (
          <select
            value={selectedSubPortId}
            onChange={(e) => setSelectedSubPortId(e.target.value)}
            className="px-4 py-2.5 rounded-lg text-sm md:text-base font-semibold border outline-none self-start"
            style={{
              backgroundColor: 'var(--bg-item)',
              borderColor: 'var(--border-item-light)',
              color: 'var(--text-color)',
            }}
          >
            <option value={OVERVIEW_VALUE}>ภาพรวมทั้งพอร์ต</option>
            {portfolios.map((sub: any) => (
              <option key={sub.subPortId} value={sub.subPortId}>
                {sub.name || sub.subPortId}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Note: โหมดง่าย (V1) คำนวณจากรายการหุ้นปัจจุบัน ไม่ใช่ transaction history เต็มรูปแบบ */}
      {displayMode === 'V1' && (
        <div
          className="mb-5 p-3 border rounded-lg text-base leading-relaxed"
          style={{
            backgroundColor: 'var(--yellow-note-bg)',
            borderColor: 'var(--yellow-note-border)',
            color: 'var(--yellow-text-color)',
          }}
        >
          <span className="font-bold">หมายเหตุ:</span> ผลตอบแทนในโหมดง่ายจะคำนวณจากรายการหุ้นที่มีอยู่ในพอร์ตปัจจุบันและข้อมูลราคาย้อนหลังนับแต่วันที่ทำการสร้างพอร์ตในระบบ
          จึงเป็นค่าประมาณสำหรับใช้ดูแนวโน้มย้อนหลัง และอาจไม่สะท้อนจังหวะการซื้อ ขาย หรือกระแสเงินสดที่เกิดขึ้นจริงทั้งหมดในอดีต
        </div>
      )}

      {/* Filters */}
      <div className="mb-5 space-y-4">
        {/* Selected symbols + add custom */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold" style={{ color: 'var(--text-color)' }}>
              สินทรัพย์เปรียบเทียบ
            </span>
            <span className="text-xs" style={{ color: 'var(--text-color)' }}>
              {selectedSymbols.length} / {MAX_SYMBOLS}
            </span>
          </div>

          <div className="flex flex-wrap gap-2 mb-2.5">
            {selectedSymbols.length === 0 && (
              <span className="text-sm italic" style={{ color: 'var(--text-color)' }}>
                ยังไม่ได้เลือก Benchmark เพิ่มเติม (จะแสดงเฉพาะ Portfolio)
              </span>
            )}
            {selectedSymbols.map((symbol, index) => {
              const meta = getSymbolMeta(symbol);
              return (
                <span
                  key={symbol}
                  className="inline-flex items-center gap-2 rounded-full pl-3 pr-2 py-1.5 text-sm border"
                  style={{ backgroundColor: 'rgba(0,0,0,0.2)', borderColor: 'var(--border-item-light)', color: 'var(--text-color)' }}
                >
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: benchmarkColor(index) }} />
                  {formatCryptoSymbolsInText(meta.displayName || symbol)}
                  <button
                    type="button"
                    onClick={() => handleRemoveSymbol(symbol)}
                    disabled={isLoading}
                    className="w-5 h-5 grid place-items-center rounded-full text-base leading-none hover:bg-white/10 disabled:opacity-50"
                    aria-label={`นำ ${cryptoPairLabel(symbol)} ออก`}
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>

          <SearchBar onSelect={handleSymbolSelect} includeCrypto />
        </div>

        {/* Catalog quick add */}
        <div>
          <span className="text-sm font-semibold block mb-2" style={{ color: 'var(--text-color)' }}>
            Benchmark ยอดนิยม
          </span>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {catalogLoading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <span key={i} className="h-9 w-24 rounded-full shrink-0 animate-pulse" style={{ backgroundColor: 'var(--bg-item)' }} />
                ))
              : catalog.map((item) => {
                  const active = selectedSymbols.includes(item.symbol);
                  const disabled = !active && selectedSymbols.length >= MAX_SYMBOLS;
                  return (
                    <button
                      key={item.symbol}
                      type="button"
                      disabled={disabled}
                      onClick={() => handleToggleCatalogSymbol(item.symbol)}
                      title={item.description}
                      className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                        active ? 'bg-yellow-400 text-black border-yellow-400' : 'hover:text-[var(--text-color)] border-transparent'
                      } disabled:opacity-40`}
                      style={!active ? { backgroundColor: 'var(--bg-item)', borderColor: 'var(--border-item-light)', color: 'var(--text-color)' } : undefined}
                    >
                      {item.displayName}
                    </button>
                  );
                })}
          </div>
          {catalogUsingFallback && (
            <div className="text-xs mt-1.5 flex items-center gap-2" style={{ color: 'var(--yellow-text-color)' }}>
              <span>ไม่สามารถโหลดรายการจาก API ได้ จึงแสดงรายการสำรอง</span>
              <button type="button" className="underline" onClick={() => setCatalogReloadTick((t) => t + 1)}>
                ลองใหม่
              </button>
            </div>
          )}
        </div>

        {/* Range */}
        <div className="flex flex-wrap gap-3">
          <div className="flex gap-1.5 flex-wrap">
            {RANGE_OPTIONS.map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => handleSelectRange(value)}
                className={`px-3.5 py-2 rounded-lg text-sm font-bold transition-all ${
                  range === value ? 'bg-yellow-400 text-black' : 'hover:text-[var(--text-color)]'
                }`}
                style={range !== value ? { backgroundColor: 'var(--bg-item)', color: 'var(--text-color)' } : undefined}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {range === 'CUSTOM' && (
          <div className="flex gap-3 items-end flex-wrap">
            <div>
              <label className="block text-xs mb-1" style={{ color: 'var(--text-color)' }}>จากวันที่</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="px-3 py-2 rounded-lg text-sm border outline-none"
                style={{ backgroundColor: 'var(--bg-item)', borderColor: 'var(--border-item-light)', color: 'var(--text-color)' }}
              />
            </div>
            <div>
              <label className="block text-xs mb-1" style={{ color: 'var(--text-color)' }}>ถึงวันที่</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="px-3 py-2 rounded-lg text-sm border outline-none"
                style={{ backgroundColor: 'var(--bg-item)', borderColor: 'var(--border-item-light)', color: 'var(--text-color)' }}
              />
            </div>
            {rangeValidationError && (
              <span className="text-sm" style={{ color: 'var(--red-text-color)' }}>{rangeValidationError}</span>
            )}
          </div>
        )}
      </div>

      {/* Chart */}
      <div
        className="relative w-full h-[440px] md:h-[520px] rounded-lg border p-3"
        style={{ borderColor: 'var(--border-item-light)' }}
      >
        {isLoading && !comparison ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center animate-pulse" style={{ color: 'var(--text-color)' }}>
            <span className="text-5xl mb-3">📊</span>
            <p className="font-bold text-lg">กำลังคำนวณเปรียบเทียบตลาด...</p>
          </div>
        ) : comparisonErrorCode && EMPTY_PORTFOLIO_CODES.has(comparisonErrorCode) ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <div className="text-6xl mb-3 opacity-80">📭</div>
            <h3 className="text-lg font-bold mb-1.5" style={{ color: 'var(--yellow-text-color)' }}>ยังไม่มีข้อมูลหุ้นในพอร์ตนี้</h3>
            <p className="text-sm" style={{ color: 'var(--text-color)' }}>
              {selectedSubPortId === OVERVIEW_VALUE ? 'พอร์ตนี้' : 'Sub-port นี้'} ยังไม่มีการซื้อขายหรือโอนหุ้นเข้ามาก่อนวันที่คำนวณ
              <br />
              กรุณาเพิ่มรายการซื้อขายหุ้นก่อน ระบบจึงจะสามารถคำนวณผลตอบแทนเปรียบเทียบให้ได้
            </p>
          </div>
        ) : comparisonError ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <div className="text-5xl mb-3 opacity-80">❗</div>
            <h3 className="text-lg font-bold mb-1.5" style={{ color: 'var(--red-text-color)' }}>โหลดข้อมูลเปรียบเทียบไม่สำเร็จ</h3>
            <p className="text-sm" style={{ color: 'var(--text-color)' }}>{comparisonError}</p>
            <button
              type="button"
              onClick={() => setReloadTick((t) => t + 1)}
              className="mt-3 px-4 py-2 rounded-lg text-sm font-semibold border"
              style={{ borderColor: 'var(--border-item-light)', color: 'var(--text-color)' }}
            >
              ลองอีกครั้ง
            </button>
          </div>
        ) : !hasChartData ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <div className="text-6xl mb-3 opacity-80">📭</div>
            <h3 className="text-lg font-bold mb-1.5" style={{ color: 'var(--yellow-text-color)' }}>ไม่มีข้อมูลในช่วงเวลานี้</h3>
            <p className="text-sm" style={{ color: 'var(--text-color)' }}>
              ลองเปลี่ยนช่วงเวลา ความละเอียดข้อมูล หรือ Symbol ที่ใช้เปรียบเทียบ
            </p>
          </div>
        ) : (
          <>
            {chartData && <Line data={chartData} options={chartOptions} />}
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div
                  className="flex items-center gap-3 px-6 py-3.5 rounded-full text-base font-bold"
                  style={{
                    backgroundColor: 'var(--bg-item)',
                    color: 'var(--text-color)',
                    boxShadow: '0 12px 36px rgba(0,0,0,0.45), 0 0 0 1px var(--border-item-light)',
                  }}
                >
                  <span
                    className="w-4 h-4 rounded-full border-[3px] border-t-transparent animate-spin"
                    style={{ borderColor: 'var(--text-color)', borderTopColor: 'transparent' }}
                  />
                  กำลังอัปเดตข้อมูล...
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Summary cards */}
      {summaryEntries.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
          {summaryEntries.map(({ meta, terminal, isPortfolio, isBest, diff, color }) => (
            <div
              key={meta.symbol}
              className={`rounded-lg p-5 border relative transition-transform ${isBest ? 'scale-[1.02]' : ''}`}
              style={{
                background: isBest
                  ? `linear-gradient(150deg, ${hexToRgba(color, 0.24)}, rgba(0,0,0,0.35) 70%)`
                  : 'rgba(0,0,0,0.2)',
                borderColor: color,
                borderWidth: isBest ? 2 : 1,
                boxShadow: isBest
                  ? `0 0 0 1px ${hexToRgba(color, 0.45)}, 0 10px 28px ${hexToRgba(color, 0.35)}`
                  : undefined,
              }}
            >
              {!isPortfolio && (
                <button
                  type="button"
                  onClick={() => handleRemoveSymbol(meta.symbol)}
                  disabled={isLoading}
                  className="absolute top-3 left-3 w-6 h-6 grid place-items-center rounded-full text-sm leading-none hover:bg-white/10 disabled:opacity-40"
                  style={{ color: 'var(--text-color)' }}
                  aria-label={`นำ ${cryptoPairLabel(meta.symbol)} ออกจากการเปรียบเทียบ`}
                  title={`นำ ${cryptoPairLabel(meta.symbol)} ออกจากการเปรียบเทียบ`}
                >
                  ×
                </button>
              )}
              {isBest && (
                <span
                  className="absolute top-3 right-3 text-xs font-bold px-2.5 py-1 rounded-full"
                  style={{ backgroundColor: 'var(--yellow-text-color)', color: '#000' }}
                >
                  ✨ สูงสุด
                </span>
              )}
              <p
                className={`text-sm font-semibold tracking-wide pr-14 ${!isPortfolio ? 'pl-7' : ''}`}
                style={{ color: 'var(--text-color)' }}
              >
                <span className="inline-block w-2.5 h-2.5 rounded-full mr-1.5 align-middle" style={{ backgroundColor: color }} />
                {isPortfolio ? portfolioDisplayLabel : `${formatCryptoSymbolsInText(meta.displayName || meta.symbol)} · ${cryptoPairLabel(meta.symbol)}`}
              </p>
              <p className="text-3xl font-bold mt-1.5" style={{ color: getReturnColor(terminal.percentValue) }}>
                {formatPercent(terminal.percentValue)}
              </p>
              <p className="text-sm mt-1" style={{ color: 'var(--text-color)' }}>
                {formatUSD(terminal.realValue)}
              </p>
              {diff !== null && (
                <p className="text-xs mt-2" style={{ color: 'var(--text-color)' }}>
                  ต่างจาก {portfolioDisplayLabel} {formatPercent(diff)}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Partial errors */}
      {comparison?.errors && comparison.errors.length > 0 && (
        <div className="mt-4 space-y-2">
          {comparison.errors.map((err, idx) => (
            <div
              key={`${err.symbol}-${idx}`}
              className="flex items-center gap-2 text-sm px-4 py-2.5 rounded-lg border"
              style={{ borderColor: 'var(--yellow-text-color)', color: 'var(--yellow-text-color)' }}
            >
              <strong>{err.symbol || 'Symbol'}</strong>
              <span>{mapSymbolError(err.code)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BenchmarkComponent;
